/**
 * The twelve "Addenda Incorporated" boxes on page 2 of the AAR
 * Residential Resale Purchase Contract.
 *
 * CONSUMER: shared (the send route, and only-what-you-know).
 *
 * ── NATE'S RULE, AND WHY IT IS DERIVABLE AT ALL ─────────────────────
 *
 * Every one of the twelve carries the same instruction, written into
 * the field atlas on 2026-09-14:
 *
 *   "all addenda boxes start unchecked; a box is ticked when that
 *    addendum is added to the package on the Forms step, never from
 *    property facts alone"
 *
 * That last clause is the important half. A house with a septic tank
 * does NOT tick the On-site Wastewater box -- adding the On-site
 * Wastewater ADDENDUM to the envelope does. So the question is about
 * this package and nothing else, which is exactly the kind of question
 * the OS can answer without asking anybody.
 *
 * It could not answer it until 2026-09-18, because a document copied
 * from a catalog template kept no record of which catalog form it was.
 * `documents.source_form_key` is that record; this module is the table
 * that turns a box's rule sentence into the form key it is asking about.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────
 *
 * Four of the twelve are NOT answered here, and each stays a question
 * the sender is asked rather than a box quietly left blank:
 *
 *   Loan Assumption · Seller Financing · Short Sale
 *     Real AAR forms that Stewardship has not added to `contract_forms`.
 *     With no catalog row there is no form key, so there is nothing to
 *     look for in the package. Adding the form to the catalog is what
 *     makes each of these derive -- add the key below in the same
 *     change, and it starts working.
 *
 *   Other
 *     An escape hatch by design: the agent ticks it and types the
 *     addendum's name in the box beside it, which the atlas marks as
 *     theirs to fill. There is no form key because there is no form.
 *
 * Saying "we don't know" for those four is the whole point. The defect
 * this replaces was the opposite: all twelve went out blank while the
 * prep screen counted all twelve among the ones it had filled.
 */

/**
 * Addendum name, exactly as the rule sentence says it, to the
 * `contract_forms.form_key` that IS that addendum.
 *
 * Keyed on the reviewer's own words rather than on a caption or a field
 * id, because the words are what a person reviewing the atlas reads and
 * what they would change. Lower-cased at lookup, so "H.O.A." and
 * "Solar Addendum" match however they were typed.
 */
import { BOLDSIGN_CHECKBOX_OFF, BOLDSIGN_CHECKBOX_ON } from "./field-types";

export const ADDENDUM_FORM_KEY_BY_NAME: ReadonlyMap<string, string> = new Map([
  ["additional clause", "additional_clause_addendum"],
  ["buyer contingency", "buyer_contingency_addendum"],
  ["domestic water well", "domestic_water_well_addendum"],
  ["h.o.a.", "hoa_condominium_planned_community_addendum"],
  ["lead-based paint disclosure", "lead_based_paint_disclosure_sales"],
  ["on-site wastewater treatment facility", "onsite_wastewater_addendum"],
  ["seller compensation", "seller_compensation_addendum"],
  ["solar addendum", "solar_addendum"],
]);

/** The name a `derive` rule for one of these boxes is asking about.
 *
 *  The atlas writes the rule as a sentence, because a sentence is what
 *  a human reviews:
 *
 *    rule: checked only when the H.O.A. addendum is in this package
 *          (offer.package.forms)
 *
 *  Null for any rule that is not one of these boxes -- which is every
 *  other derive rule in the atlas, and is why this can be asked of a
 *  field without knowing in advance what kind it is. */
export function addendumNameInRule(fillRule: string | null | undefined): string | null {
  const text = fillRule?.trim();
  if (!text) return null;
  if (!text.includes("offer.package.forms")) return null;
  // "the <name> addendum is in this package". Non-greedy so a sentence
  // that mentions a second addendum later cannot swallow it.
  const m = /\bthe\s+(.+?)\s+addendum\s+is\s+in\s+this\s+package/i.exec(text);
  const name = m?.[1]?.trim();
  return name ? name : null;
}

/** The catalog form a box is asking about, or null when there is none to
 *  ask about (the four named in this module's header). */
export function addendumFormKeyForRule(fillRule: string | null | undefined): string | null {
  const name = addendumNameInRule(fillRule);
  if (!name) return null;
  return ADDENDUM_FORM_KEY_BY_NAME.get(name.toLowerCase()) ?? null;
}

/**
 * What this box should say, given the catalog forms actually in the
 * envelope.
 *
 * Three answers, and the difference between the last two is the whole
 * reason this returns a union rather than a boolean:
 *
 *   "on"   tick it -- that addendum is in this package
 *   "off"  leave it -- it is not, and an unticked box is a real answer
 *   null   not a box this module can answer, so ASK
 *
 * "off" is a DECIDED no, not an absence, which is the distinction
 * field-types.ts keeps these two constants for. It matters twice: an
 * unticked box is stamped as nothing at all (the printed form already
 * shows an empty square, and drawing into it would assert something
 * nobody said), and a preparer's box with no value at all stops the
 * send -- correctly, since nobody downstream can fill it.
 */
export function addendaBoxValue(
  fillRule: string | null | undefined,
  formKeysInPackage: ReadonlySet<string>,
): string | null {
  const key = addendumFormKeyForRule(fillRule);
  if (!key) return null;
  return formKeysInPackage.has(key) ? BOLDSIGN_CHECKBOX_ON : BOLDSIGN_CHECKBOX_OFF;
}

/** Every catalog form in one envelope, read off the documents the send
 *  is about to mail.
 *
 *  A document with no key is simply not a catalog form — a hand-uploaded
 *  pre-qual letter, a scan, anything copied before the column existed.
 *  It contributes nothing rather than being guessed at. */
export function formKeysInPackage(
  documents: readonly { source_form_key?: string | null }[],
): Set<string> {
  const keys = new Set<string>();
  for (const d of documents) {
    const key = d.source_form_key?.trim();
    if (key) keys.add(key);
  }
  return keys;
}

/**
 * Tick the addenda boxes this envelope has earned, and leave everything
 * else exactly as the sender left it.
 *
 * Returns new field objects rather than mutating, because the caller
 * keeps the fields AS PLACED to teach the saved layout: what a map
 * remembers is where the boxes are, never what one envelope happened to
 * contain. Teaching from the ticked copy would bake this deal's addenda
 * onto every future contract.
 *
 * A field this module cannot answer is returned UNCHANGED, not blanked.
 * That is what keeps the four unanswerable boxes questions the sender is
 * asked instead of boxes quietly emptied.
 */
export function tickAddendaBoxes<T extends { fillRule?: string | null; value?: string | null }>(
  fields: readonly T[],
  formKeys: ReadonlySet<string>,
): T[] {
  return fields.map((f) => {
    const value = addendaBoxValue(f.fillRule, formKeys);
    return value === null ? f : { ...f, value };
  });
}
